import { UniqMap } from 'src/lib/UniqMap';

import {
  SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_PLAN_ACTIVE,
  SUBSCRIPTION_PLAN_CATEGORY,
  SUBSCRIPTION_PLAN_LEGACY,
  SUBSCRIPTION_SEAT_TIER,
  type SubscriptionPlan,
  type SubscriptionPlanCategory,
  type SubscriptionPlanLegacy,
  type SubscriptionTier,
} from './const';

export const isSubscriptionPlanDeprecated = (plan: string): boolean =>
  Object.values(SUBSCRIPTION_PLAN_LEGACY).includes(plan as SubscriptionPlanLegacy);

const isPlanFromCategory = (plan: SubscriptionPlan, planType: SubscriptionPlanCategory): boolean =>
  getSubscriptionPlanCategory(plan) === planType;

export const isPlanFromBasicCategory = (plan: SubscriptionPlan): boolean =>
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.basic);
export const isPlanFromStarterCategory = (plan: SubscriptionPlan): boolean =>
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.starter);
export const isPlanFromDataDrivenCategory = (plan: SubscriptionPlan): boolean =>
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.dataDriven);
export const isPlanFromBusinessCategory = (plan: SubscriptionPlan): boolean =>
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.business);

// Enterprise and POC plans are considered as the same category
export const isPlanFromEnterpriseCategory = (plan: SubscriptionPlan): boolean =>
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.enterprise) ||
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.poc);

export const isPocPlan = (plan: SubscriptionPlan): boolean => plan === SUBSCRIPTION_PLAN.pocLegacy;

export const isPlanFromBasicOrStarterCategory = (plan: SubscriptionPlan): boolean =>
  isPlanFromBasicCategory(plan) || isPlanFromStarterCategory(plan);
export const isPlanFromStarterOrDataDrivenCategory = (plan: SubscriptionPlan): boolean =>
  isPlanFromStarterCategory(plan) || isPlanFromDataDrivenCategory(plan);
export const isPlanFromBusinessOrEnterpriseCategory = (plan: SubscriptionPlan): boolean =>
  isPlanFromBusinessCategory(plan) || isPlanFromEnterpriseCategory(plan);
export const isPlanWithSeatTiers = (plan: SubscriptionPlan): boolean =>
  isPlanFromDataDrivenCategory(plan) || isPlanFromBusinessCategory(plan);

export const planCanBeSelfPurchased = (plan: SubscriptionPlan): boolean =>
  !isPlanFromBusinessOrEnterpriseCategory(plan);
export const planCanBeCancelled = (plan: SubscriptionPlan): boolean =>
  isPlanFromStarterCategory(plan) ||
  isPlanFromDataDrivenCategory(plan) ||
  isPlanFromBusinessCategory(plan);
export const planCanBeUpgraded = (plan: SubscriptionPlan): boolean =>
  !isPlanFromEnterpriseCategory(plan);
export const shouldUpgradeToBusiness = (plan: SubscriptionPlan): boolean =>
  isPlanFromStarterCategory(plan) ||
  isPlanFromBasicCategory(plan) ||
  isPlanFromDataDrivenCategory(plan);

interface PlanProps {
  category: SubscriptionPlanCategory;
  label: string;
  subtitle?: string;
  description?: string;
  tiers?: Record<string, SubscriptionTier>;
  seats?: number;
  price?: number;
  priceAnnually?: number;
  priceAnnuallyPerMonth?: number;
  percentOfAnnualDiscount?: number;
  performancePredictionsLimit?: number;
}

const SUBSCRIPTION_PLAN_PROPS = new UniqMap<SubscriptionPlan, PlanProps>();

/**
 * Active plans
 */

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.basic, {
  category: SUBSCRIPTION_PLAN_CATEGORY.basic,
  label: 'Free',
  // Nominal basic (Free) plan limit. Trial customers get 25 via a lifecycle-aware
  // backend override (Creator::Customers::FeatureLimits::GetLimits) surfaced through
  // the live usage meter — not this catalog value, which represents the paid Free plan.
  performancePredictionsLimit: 25,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.starter, {
  category: SUBSCRIPTION_PLAN_CATEGORY.starter,
  label: 'Starter',
  description:
    'For solo marketers & freelancers who want on-brand content backed by Anyword’s performance data.',
  performancePredictionsLimit: 50,
  seats: 1,
  price: 49,
  priceAnnually: 468,
  priceAnnuallyPerMonth: 39,
  percentOfAnnualDiscount: 20,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.dataDriven, {
  category: SUBSCRIPTION_PLAN_CATEGORY.dataDriven,
  label: 'Data-Driven',
  description:
    'For marketing teams who want on-brand content backed by Anyword’s performance data.',
  tiers: SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
  performancePredictionsLimit: 100,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.business, {
  category: SUBSCRIPTION_PLAN_CATEGORY.business,
  label: 'Business',
  description:
    'For marketing teams who want better performance across channels with AI trained on your best messaging & past campaigns.',
  tiers: SUBSCRIPTION_SEAT_TIER,
  performancePredictionsLimit: 250,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.businessPlus, {
  category: SUBSCRIPTION_PLAN_CATEGORY.business,
  label: 'Business Plus',
  performancePredictionsLimit: 375,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.businessMax, {
  category: SUBSCRIPTION_PLAN_CATEGORY.business,
  label: 'Business Max',
  performancePredictionsLimit: 500,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.performance, {
  category: SUBSCRIPTION_PLAN_CATEGORY.enterprise,
  label: 'Enterprise',
  description:
    'For teams who want custom-built AI tailored to their brand, with enterprise-grade security, privacy, and control.',
  performancePredictionsLimit: 500,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.performancePlus, {
  category: SUBSCRIPTION_PLAN_CATEGORY.enterprise,
  label: 'Enterprise Plus',
  performancePredictionsLimit: 1000,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.performanceMax, {
  category: SUBSCRIPTION_PLAN_CATEGORY.enterprise,
  label: 'Enterprise Max',
  performancePredictionsLimit: 2000,
});

/**
 * Legacy plans
 */
SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.basicTieredLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.basic,
  label: 'Basic Tiered - legacy',
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.starterLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.starter,
  label: 'Starter - legacy',
  description:
    'For solo marketers & freelancers who want on-brand content backed by Anyword’s performance data.',
  seats: 1,
  price: 49,
  priceAnnually: 468,
  priceAnnuallyPerMonth: 39,
  percentOfAnnualDiscount: 20,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.ddTeamsLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.dataDriven,
  label: 'Data-Driven - legacy',
  description:
    'For marketing teams who want on-brand content backed by Anyword’s\nperformance data.',
  tiers: SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.businessSelfServeLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.business,
  label: 'Business - legacy',
  description:
    'For marketing teams who want better performance across channels with AI trained on your best messaging & past campaigns.',
  tiers: SUBSCRIPTION_SEAT_TIER,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.proPublisherLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.business,
  label: 'ProPublisher - legacy',
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.pocLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.poc,
  label: 'Proof of Concept - legacy',
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.businessLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.enterprise,
  label: 'Enterprise',
  description:
    'For teams who want custom-built AI tailored to their brand, with enterprise-grade security, privacy, and control.',
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.enterpriseLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.enterprise,
  label: 'Enterprise',
});

export const getPlanProperty = <T>(
  plan: SubscriptionPlan,
  property: keyof PlanProps,
  defaultValue?: T
): T | undefined => {
  const props = SUBSCRIPTION_PLAN_PROPS.get(plan);
  if (props && props[property] != null) {
    return props[property] as T;
  }
  return defaultValue;
};

export const getSubscriptionPlanLabel = (plan: SubscriptionPlan) =>
  getPlanProperty<string>(plan, 'label', plan || '');

export const getSubscriptionPlanCategory = (plan: SubscriptionPlan) =>
  getPlanProperty<SubscriptionPlanCategory>(plan, 'category');

export const getSubscriptionPlanSubtitle = (plan: SubscriptionPlan) =>
  getPlanProperty<string>(plan, 'subtitle', '');

export const getSubscriptionPlanDescription = (plan: SubscriptionPlan) =>
  getPlanProperty<string>(plan, 'description', '');

export const getSubscriptionPlanTiers = (plan: SubscriptionPlan) =>
  getPlanProperty<Record<string, SubscriptionTier>>(plan, 'tiers');

export const getPlanPerformancePredictionsLimit = (plan: SubscriptionPlan) =>
  getPlanProperty<number>(plan, 'performancePredictionsLimit', 0);
